// src/pages/StudentLogin.jsx — the STUDENT login door (/signin).
// Role-gated: only accounts that have a students/{uid} profile may enter here.
// An admin (no student profile) is rejected and signed back out.
import React, { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { HiMail, HiLockClosed } from "react-icons/hi";
import { auth, db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import { friendlyAuthError } from "../lib/authErrors";
import AuthLayout from "../components/auth/AuthLayout";
import AuthField from "../components/auth/AuthField";
import { SEO } from "../components/common/SEO";

const PANEL_BULLETS = [
  "Track your XP, levels & daily streaks",
  "Climb the class leaderboard",
  "Personalized practice for your weak topics",
];

export default function StudentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");           // top-level form error
  const [fieldErrors, setFieldErrors] = useState({}); // { email, password }
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address.";
    if (!password) errs.password = "Password is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db, "students", user.uid));

      if (!snap.exists()) {
        // An admin tried the student door — reject.
        await signOut(auth);
        setError("This login is for students only. Please use the staff login.");
        return;
      }
      if (snap.data().active === false) {
        await signOut(auth);
        setError("Your account is inactive. Please contact your tutor.");
        return;
      }

      await refreshProfile();
      navigate("/dashboard");
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      accent="student"
      panelTitle="Welcome back to your learning journey."
      panelSubtitle="Sign in to pick up where you left off and keep your streak alive."
      bullets={PANEL_BULLETS}
    >
      <SEO title="Student Login" description="Log in to your student learning dashboard." canonical="/signin" />

      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-gray-800">Student Login</h2>
        <p className="text-sm text-gray-400 mt-1">Sign in to track your progress</p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} noValidate>
        <AuthField
          id="email"
          label="Email"
          type="email"
          icon={HiMail}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          error={fieldErrors.email}
        />
        <AuthField
          id="password"
          label="Password"
          type="password"
          icon={HiLockClosed}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          autoComplete="current-password"
          required
          error={fieldErrors.password}
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-indigo-200 flex items-center justify-center gap-2"
        >
          {loading && (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400 mt-6">
        Don't have an account? Ask your tutor to create one for you.
      </p>
    </AuthLayout>
  );
}
